/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Autocomplete,
  Checkbox,
  FormControlLabel,
  Grid,
  InputLabel,
  Paper,
  TextField,
} from "@mui/material";
import { OneAlertValidationSchema } from "../../Utils/Models";
import { Vocabulary } from "../../Utils/Vocabulary";
import { SketchPicker } from "react-color";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { OneAlertProps } from "../../Utils/Types";
import { FormsIds } from "../../Utils/Constants";
import useCustomMemo from "../../Hooks/useCustomMemo";
import { postData } from "../../Services/postData";
import { endpoints } from "../../Utils/UrlEnum";
import { onFormError } from "../../Utils/Utils";

export default function OneAlert(props: OneAlertProps) {
  const { propertyOptions, ruleOptions, onClose } = props;
  const memo = useCustomMemo();
  const roles = memo?.storage.roles || [];
  const { control, formState, getValues, watch, handleSubmit } = useForm({
    resolver: yupResolver(OneAlertValidationSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    context: undefined,
    shouldFocusError: true,
    shouldUnregister: false,
    criteriaMode: "firstError",
  });

  /**
   *
   * @param data
   */
  const addNewAlert = (data: any) => {
    postData(endpoints.alert.main, data).then((response) => {
      if (response && response.data) {
        onClose();
      }
    });
  };

  return (
    <form
      id={FormsIds.alertForm}
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        handleSubmit(addNewAlert, onFormError)(e);
      }}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} md={6} lg={6}>
          <Controller
            name={"name"}
            control={control}
            render={({ field: props }) => (
              <TextField
                {...props}
                variant="outlined"
                name="name"
                size="small"
                id="name"
                required
                label={Vocabulary.name}
                fullWidth
                error={typeof formState.errors.name === "object"}
                helperText={formState.errors.name?.message?.toString()}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          <Controller
            name="roles"
            control={control}
            defaultValue={undefined}
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <Autocomplete
                freeSolo={false}
                disablePortal
                multiple
                noOptionsText={Vocabulary.noResultsFound}
                fullWidth
                getOptionDisabled={(option: any) =>
                  !!getValues("roles")?.find(
                    (element: any) => element._id === option._id
                  )
                }
                onChange={(event, value) => field.onChange(value)}
                value={getValues("roles") || []}
                options={roles}
                isOptionEqualToValue={(option, value) => option?._id === value}
                getOptionLabel={(option: any) => option.name}
                id="roles"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={!!error}
                    helperText={error?.message}
                    name="roles"
                    // required
                    size="small"
                    variant="outlined"
                    inputRef={ref}
                    label={Vocabulary.sendNotificationsTo}
                  />
                )}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          <Controller
            name="property"
            control={control}
            defaultValue={undefined}
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <Autocomplete
                freeSolo={false}
                disablePortal
                noOptionsText={Vocabulary.noResultsFound}
                fullWidth
                onChange={(event, value) => field.onChange(value)}
                value={getValues("property")}
                options={propertyOptions}
                isOptionEqualToValue={(option, value) => option === value}
                getOptionLabel={(option) => Vocabulary[option] || option}
                id="property"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={!!error}
                    helperText={error?.message}
                    name="property"
                    size="small"
                    required
                    variant="outlined"
                    inputRef={ref}
                    label={Vocabulary.property}
                  />
                )}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={6} lg={6}>
          <Controller
            name="rule"
            control={control}
            defaultValue={undefined}
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <Autocomplete
                freeSolo={false}
                disablePortal
                noOptionsText={Vocabulary.noResultsFound}
                fullWidth
                onChange={(event, value) => field.onChange(value?.value)}
                options={ruleOptions}
                isOptionEqualToValue={(option, value) => option === value}
                getOptionLabel={(option) => option.label}
                id="rule"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={!!error}
                    helperText={error?.message}
                    name="rule"
                    required
                    size="small"
                    variant="outlined"
                    inputRef={ref}
                    label={Vocabulary.rule}
                  />
                )}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          <Controller
            name={"threshold"}
            control={control}
            render={({ field: props }) => (
              <TextField
                {...props}
                name="threshold"
                id="threshold"
                size="small"
                required
                label={Vocabulary.threshold}
                fullWidth
                error={typeof formState.errors.threshold === "object"}
                helperText={formState.errors.threshold?.message?.toString()}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          <FormControlLabel
            label={Vocabulary.sendEmail}
            control={
              <Controller
                defaultValue={false}
                name={`sendEmail`}
                control={control}
                render={({ field: props }) => (
                  <Checkbox
                    {...props}
                    checked={watch(`sendEmail`)}
                    onChange={(e) => props.onChange(e.target.checked)}
                  />
                )}
              />
            }
          />
        </Grid>
        <Grid item xs={12} md={12} lg={12}>
          <InputLabel style={{ margin: "10px 0px" }}>
            {Vocabulary.chooseColor}
          </InputLabel>
          <div
            style={{
              display: "flex",
            }}
          >
            <Controller
              name={"color"}
              control={control}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <SketchPicker
                  color={watch("color")}
                  onChangeComplete={(color: any) => field.onChange(color.hex)}
                />
              )}
            />
            <Paper
              style={{
                width: 50,
                height: 50,
                margin: "0px 30px",
                backgroundColor: watch("color"),
              }}
            ></Paper>
          </div>
        </Grid>
      </Grid>
    </form>
  );
}
